/**
 * Small internal utilities shared across the legend entry extractors.
 * None of these are part of the public API.
 */

import type {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  LayerSpecification,
} from "maplibre-gl";

import { hexToRgb } from "@nens/shared-utils";

import type { SwatchType } from "../types/legend-types";

// ---------------------------------------------------------------------------
// Paint accessors
// ---------------------------------------------------------------------------

/** Returns the primary color paint property for fill, line, and circle layers. */
export function getColorPaint(
  layer: LayerSpecification
): DataDrivenPropertyValueSpecification<string> | undefined {
  switch (layer.type) {
    case "fill":
      return layer.paint?.["fill-color"];
    case "line":
      return layer.paint?.["line-color"];
    case "circle":
      return layer.paint?.["circle-color"];
    default:
      return undefined;
  }
}

/** Maps a layer type to the swatch shape used in the legend, or null for unsupported types. */
export function getSwatchType(layer: LayerSpecification): SwatchType | null {
  if (layer.type === "fill") return "fill";
  if (layer.type === "line") return "line";
  if (layer.type === "circle") return "circle";
  return null;
}

/**
 * Returns the fill-pattern or line-pattern value if it is a plain string (not an expression).
 * Returns null when the property is absent or data-driven.
 */
export function getPatternPaint(layer: LayerSpecification): string | null {
  if (layer.type === "fill") {
    const p = layer.paint?.["fill-pattern"];
    return typeof p === "string" ? p : null;
  }
  if (layer.type === "line") {
    const p = layer.paint?.["line-pattern"];
    return typeof p === "string" ? p : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Layer metadata
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable label for a layer, in order of preference:
 *   1. `metadata["legend:label"]` — explicit override on the layer.
 *   2. GeoServer named rule: `"prefix:(rule#N)Label text:index"` → `"Label text"`.
 *   3. Empty string (no label).
 */
export function getLayerLabel(layer: LayerSpecification): string {
  const metadata = layer.metadata as Record<string, unknown> | undefined;
  const label = metadata?.["legend:label"];
  if (typeof label === "string") return label;

  // GeoServer named rules: "prefix:(rule#N)Label text:index" → "Label text"
  const namedMatch = /\)\s*(.+?):\d+$/.exec(layer.id);
  if (namedMatch) {
    return namedMatch[1].trim();
  }

  return "";
}

/** Returns the `source-layer` of a layer, falling back to the layer ID. */
export function getSourceLayer(layer: LayerSpecification): string {
  return "source-layer" in layer &&
    layer["source-layer"] &&
    typeof layer["source-layer"] === "string"
    ? layer["source-layer"]
    : layer.id;
}

// ---------------------------------------------------------------------------
// Color utilities
// ---------------------------------------------------------------------------

/**
 * Bakes an opacity value into a CSS hex color, returning an `rgba()` string.
 * Returns the original color unchanged if opacity is 1 or the hex cannot be parsed.
 */
export function applyOpacityToColor(color: string, opacity: number): string {
  if (opacity === 1) return color;
  const [r, g, b] = hexToRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// ---------------------------------------------------------------------------
// Expression guard
// ---------------------------------------------------------------------------

/**
 * Returns true if `v` is a MapLibre expression array (i.e. first element is a string operator).
 * Used to distinguish expressions from plain value arrays such as `line-dasharray: [8, 5]`.
 */
export function isExpression(v: unknown): v is ExpressionSpecification {
  return Array.isArray(v) && v.length > 0 && typeof v[0] === "string";
}
